/**
 * Talking to `/api/account/stats`.
 *
 * A plain module with no rune in it — the same reason `daily-io.ts` and
 * `admin-io.ts` are — and reusing `account-io.ts`'s `request()` rather than a
 * fourth copy of "credentials, status mapping, never throw for a reason a
 * player caused."
 */
import { request } from './account-io.js';
import type { AccountOutcome } from './account-io.js';
import type { ModeStats } from './solo-stats-io.js';

export const ACCOUNT_STATS_PATH = '/api/account/stats';

/** One mode's lifetime totals, as the server names it — `modeId` plus `ModeStats`'s own fields. */
export interface AccountSoloStats extends ModeStats {
  readonly modeId: string;
}

/**
 * One variant's win/loss/draw record against one kind of opponent, derived
 * server-side from `match`/`match_participant`.
 *
 * Two rows per variant at most — people and the AI, never summed. Owner
 * overhaul part 11 and `apps/server/src/accounts/model.ts#VariantRecord`.
 * Optional here because a server that predates the split simply does not send
 * it, and its rows are all human matches by construction.
 */
export interface AccountVariantStats {
  readonly variant: string;
  readonly vsAi?: boolean;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

export interface AccountStats {
  readonly solo: readonly AccountSoloStats[];
  readonly versus: readonly AccountVariantStats[];
}

/** Injected in tests; defaults to the page's own `fetch`. */
export type Fetcher = typeof globalThis.fetch;

/** Both halves at once — signed-in only, the same `signed-out` outcome every other `/api/account/*` route answers with for a guest. */
export function fetchAccountStats(fetcher?: Fetcher): Promise<AccountOutcome<AccountStats>> {
  return request(ACCOUNT_STATS_PATH, {
    method: 'GET',
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}

/** Merge a device's local solo totals up, and take back whatever now stands. */
export function pushSoloStats(
  solo: Readonly<Record<string, ModeStats>>,
  fetcher?: Fetcher,
): Promise<AccountOutcome<{ solo: readonly AccountSoloStats[] }>> {
  return request(ACCOUNT_STATS_PATH, {
    method: 'PUT',
    body: { solo },
    ...(fetcher === undefined ? {} : { fetcher }),
  });
}
